import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import ChatDateDividerPresenter from '../ChatBubble/ChatDateDividerPresenter';
import DropzoneProvider from './DropzoneProvider';
import ChatMessageUploadQueue from './ChatMessageUploadQueue';
import ChatMessageInput from './ChatMessageInput';
import ReviewWriteDialog from '@components/Dialog/Review/ReviewWriteDialog';
import { Box } from '@material-ui/core';

import {
  CapaQuotationUserRfqDetail,
  FetchQuotationChatMessagesQuery,
  Maybe
} from '@generated/graphql';
import { colorSet } from '@components/Provider';

import { CircularProgress } from '@material-ui/core';

import {
  isApplicableClientType,
  isCommonMessageType,
  isNextDay
} from '../utils';
import { SubscribeMessage } from '@modules/chatMessages';
import BubbleMemoComponent from './BubbleMemoComponent';
import useStyles from '../useStyles';
import { CustomizedTypography } from '@components/Customized';
import { InformationFilled } from '@carbon/icons-react';

interface BypassChatEvent extends Event {
  detail: { bypassSuspect: boolean };
}

interface Props {
  matches: boolean;
  loading: boolean;
  chatRoomId: string;
  messages?: FetchQuotationChatMessagesQuery['fetchQuotationChatMessages']['data'];
  rfqDetail?: CapaQuotationUserRfqDetail;
  partnerLastSeenAt?: string;
  subscribeMessages: SubscribeMessage[];
}

const ChatMessagePresenter = ({
  loading,
  matches,
  chatRoomId,
  messages,
  rfqDetail,
  subscribeMessages
}: Props) => {
  const owner = 'CLIENT';
  const classes = useStyles();
  const uploadRef = useRef<HTMLInputElement>(null);
  const messageRootRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (messageRootRef.current) {
      messageRootRef.current.scrollTop = messageRootRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const bypassSuspectHandler = (e: Event) => {
      if (!messageRootRef.current) return;
      scrollToBottom();
      messageRootRef.current.classList.toggle(
        'bypass-warning',
        (e as BypassChatEvent).detail.bypassSuspect
      );
    };
    window.scrollTo(0, 1000);
    window.addEventListener('bypassChat', bypassSuspectHandler);
    return () => window.removeEventListener('bypassChat', bypassSuspectHandler);
  }, [scrollToBottom]);

  const fetchedMessagesBubbles = useMemo(
    () =>
      messages?.map((message, index) => {
        const isOwner = owner === message.senderUserType;

        const messageType = message.message?.messageType ?? undefined;

        const isApplicableMessage = isApplicableClientType(messageType);

        const isCommonMessage = isCommonMessageType(messageType);

        const renderDateDivider = isNextDay(
          message.createdAt,
          messages[index + 1]?.createdAt
        );

        if (!isApplicableMessage) return null;

        return (
          <BubbleMemoComponent
            key={message?.id}
            isCommonMessage={isCommonMessage}
            isOwner={isOwner}
            message={message}
            renderDateDivider={renderDateDivider}
            rfqDetail={rfqDetail}
          />
        );
      }),
    [messages, rfqDetail]
  );

  const isBorderSubscribeMessageBetweenFetchMessage = isDividerBetweenSubscribeMessageAndFetchMessage(
    messages,
    subscribeMessages
  );
  useEffect(() => {
    if (messageRootRef.current && messageRef.current) {
      if (
        messageRootRef.current.scrollTop >
        messageRootRef.current.scrollHeight -
          messageRef.current.scrollHeight * 20
      ) {
        scrollToBottom();
      }
    }
  }, [subscribeMessages, messageRootRef, scrollToBottom]);

  return (
    <>
      <ReviewWriteDialog />
      <DropzoneProvider ref={uploadRef}>
        <div
          ref={messageRootRef}
          className={clsx([classes['Chat-Messages-root']])}
        >
          {loading ? (
            <div className="loading">
              <CircularProgress />
            </div>
          ) : null}

          <ChatMessageUploadQueue chatRoomId={chatRoomId} />

          {(subscribeMessages ?? []).map((subscribeMessage, index) => {
            const isOwner = owner === subscribeMessage.senderUserType;

            const messageType =
              subscribeMessage.message?.messageType ?? undefined;

            const isApplicableMessage = isApplicableClientType(messageType);

            const isCommonMessage = isCommonMessageType(messageType);

            const renderDateDivider =
              !!index &&
              isNextDay(
                subscribeMessage.createdAt,
                subscribeMessages[index - 1]?.createdAt
              );

            if (!isApplicableMessage) return null;

            return (
              <BubbleMemoComponent
                key={subscribeMessage?.id}
                isCommonMessage={isCommonMessage}
                isOwner={isOwner}
                message={subscribeMessage}
                renderDateDivider={renderDateDivider}
                rfqDetail={rfqDetail}
              />
            );
          })}

          {isBorderSubscribeMessageBetweenFetchMessage && (
            <ChatDateDividerPresenter
              createdAt={subscribeMessages?.[0]?.createdAt ?? undefined}
              unread={false}
            />
          )}

          {!loading && fetchedMessagesBubbles}

          <Box width="100%">
            <Box
              bgcolor="#DEE2E6"
              padding="12px"
              display="flex"
              justifyContent="center"
              gridGap="10px"
              margin={matches ? '16px' : '16px 60px'}
              borderRadius={8}
            >
              <InformationFilled
                style={{
                  marginTop: 2,
                  minWidth: 14,
                  minHeight: 14
                }}
                color={colorSet.gray1000}
              />
              <CustomizedTypography
                variant="body2"
                style={{
                  wordBreak: 'break-all'
                }}
              >
                대화 내용은 거래유지 및 거래 지속성을 보호하고, 분쟁조정 및
                부정행위 방지 등을 위해 저장되고 활용될 수 있습니다. 캐파결제가
                아닌 직접 결제로 인한 피해에 대하여 캐파에서 도움을 드릴 수
                없으며, 직접 결제를 하거나 직접 결제를 유도하는 경우 서비스 이용
                정책에 따라 서비스 이용이 제한될 수 있습니다.
              </CustomizedTypography>
            </Box>
          </Box>
        </div>

        <ChatMessageInput
          matches={matches}
          loading={loading}
          scrollToBottom={scrollToBottom}
          onOpen={() => {
            if (uploadRef.current) {
              uploadRef.current.click();
            }
          }}
        />
      </DropzoneProvider>
    </>
  );
};

export default ChatMessagePresenter;

const isDividerBetweenSubscribeMessageAndFetchMessage = (
  messages: FetchQuotationChatMessagesQuery['fetchQuotationChatMessages']['data'],
  subscribeMessages: Maybe<SubscribeMessage[]>
) =>
  isNextDay(
    messages && messages[0] && messages[0]?.createdAt,
    subscribeMessages && subscribeMessages[0] && subscribeMessages[0].createdAt
  );
